import { z } from 'zod';
/**
 * Finance (Spesen / expense tracker — WS-4a) contracts. Shared so the CRM
 * frontend validates with the exact schemas the backend enforces. All routes are
 * session + RBAC protected and app-layer own-scoped (each staff member logs their
 * OWN Spesen; see the backend finance module). The admin `Finanzen` dashboard
 * (commission + monthly costs + balance) is a SEPARATE later PR (WS-4b).
 */
/**
 * Currencies a Spese may be booked in. This is a Swiss (CHF) shop and the
 * `summary()` endpoint sums all amounts 1:1 under a CHF label, so multi-currency
 * would silently mis-total. Restricted to CHF only; the DB keeps the `currency`
 * column (default CHF) for forward-compatibility.
 */
export const SPESE_CURRENCIES = ['CHF'];
export const speseCurrencySchema = z.enum(SPESE_CURRENCIES);
const descriptionSchema = z.string().trim().min(1).max(500);
const categorySchema = z.string().trim().min(1).max(80);
/**
 * A monetary amount in major units (e.g. 12.50 CHF). Coerced from a string
 * because Spesen are created via multipart/form-data (the receipt file rides
 * along), so every text field arrives as a string. At most two decimal places
 * (the DB column is Decimal(14,2)); the tolerant check avoids binary-float
 * false negatives (e.g. 12.10 * 100).
 */
const amountSchema = z.coerce
    .number({ error: 'amount_invalid' })
    .positive({ message: 'amount_positive' })
    .max(1_000_000, { message: 'amount_too_large' })
    .refine((n) => Number.isFinite(n) && Math.abs(n * 100 - Math.round(n * 100)) < 1e-6, {
    message: 'amount_max_2_decimals',
});
/**
 * Create a Spese (POST /finance/spesen — multipart). `spentAt` is the expense
 * DATE (used for monthly bucketing), coerced from the ISO string the DatePicker
 * sends. The optional receipt file is handled by the FileInterceptor, not this
 * schema.
 */
export const speseCreateSchema = z.object({
    amount: amountSchema,
    currency: speseCurrencySchema.default('CHF'),
    description: descriptionSchema,
    category: categorySchema.optional(),
    spentAt: z.coerce.date(),
});
/** Partial Spese update (PATCH /finance/spesen/:id). JSON body. */
export const speseUpdateSchema = z
    .object({
    amount: amountSchema,
    currency: speseCurrencySchema,
    description: descriptionSchema,
    category: categorySchema.nullable(),
    spentAt: z.coerce.date(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
/**
 * Spese list query (GET /finance/spesen). `from`/`to` filter on `spentAt`
 * (inclusive lower / exclusive upper). `mine=true` restricts an admin to their
 * OWN Spesen; a non-admin is always own-scoped server-side regardless.
 */
export const speseListQuerySchema = z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    mine: z.preprocess((v) => (typeof v === 'string' ? v === 'true' : v), z.boolean().optional()),
});
/** Monthly summary query (GET /finance/spesen/summary). Defaults to this year. */
export const speseSummaryQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    mine: z.preprocess((v) => (typeof v === 'string' ? v === 'true' : v), z.boolean().optional()),
});
// ─────────────────────────── Admin Finanzen (WS-4b) ─────────────────────────
//
// The admin-only dashboard on top of Spesen: it derives an EXPECTED commission
// from a won sales mandate (never stored — computed from the price × the rate),
// books the ACTUAL incoming payments as `FinancePayment` rows, tracks recurring
// `MonthlyCost` fixed costs, and shows a per-month balance (incoming − outgoing).
// Every route here is `@Roles('admin')` on the backend + admin-gated in the CRM.
/**
 * Which commission rate applies to a mandate. `extern` = a normal external
 * client mandate (3%); `intern` = an in-house / partner deal (5%). Stored on the
 * mandate as a nullable String (null = not yet classified → the expected
 * commission falls back to the extern rate). App-validated with this schema.
 */
export const COMMISSION_SIDES = ['extern', 'intern'];
export const commissionSideSchema = z.enum(COMMISSION_SIDES);
/** Commission of the sale price: 3% for an external mandate. */
export const COMMISSION_RATE_EXTERN = 3;
/** Commission of the sale price: 5% for an internal / in-house mandate. */
export const COMMISSION_RATE_INTERN = 5;
/** The rate (in %) that applies for a given side (null/unset → extern 3%). */
export function commissionRate(side) {
    return side === 'intern' ? COMMISSION_RATE_INTERN : COMMISSION_RATE_EXTERN;
}
/** Coerce a Decimal-ish value (number | numeric string) to a finite number or null. */
function toFiniteNumber(v) {
    if (v === null || v === undefined)
        return null;
    const n = typeof v === 'string' ? Number(v) : v;
    return typeof n === 'number' && Number.isFinite(n) ? n : null;
}
/**
 * The EXPECTED commission for a won mandate — pure + single-sourced so the
 * backend forecast, the CRM "erwartete Provision" column and the payment-modal
 * prefill can never diverge. Base = the realised `achievedPrice` if known, else
 * the `richtpreis` (asking price) as a forecast; × the side rate (5% intern /
 * 3% extern or unset), rounded to Rappen. Returns `null` when no price is known.
 */
export function computeExpectedCommission(input) {
    const base = toFiniteNumber(input.achievedPrice) ?? toFiniteNumber(input.richtpreis);
    if (base === null)
        return null;
    // base × rate / 100, rounded to cents: (base*rate/100)*100 = base*rate.
    return Math.round(base * commissionRate(input.commissionSide)) / 100;
}
/**
 * A CHF money amount for the admin finance forms (major units, ≤ 2 decimals).
 * Same tolerant two-decimal check as the Spese `amountSchema`; a wider ceiling
 * because a commission payment can be large (e.g. 5% of a multi-million sale).
 */
const moneySchema = z.coerce
    .number({ error: 'amount_invalid' })
    .positive({ message: 'amount_positive' })
    .max(100_000_000, { message: 'amount_too_large' })
    .refine((n) => Number.isFinite(n) && Math.abs(n * 100 - Math.round(n * 100)) < 1e-6, {
    message: 'amount_max_2_decimals',
});
const labelSchema = z.string().trim().min(1).max(120);
/** Dashboard/report window (GET /finance/dashboard). Defaults to this year. */
export const financeDashboardQuerySchema = z.object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
});
/**
 * Set a mandate's commission classification (PATCH
 * /finance/mandates/:id/commission). `commissionSide` reclassifies extern/intern;
 * `achievedPrice` records the realised sale price (nullable → clears it, back to
 * the richtpreis forecast). STATUS-NEUTRAL: editing these fields never changes
 * the mandate status — closing a deal as won is a separate, guarded action (POST
 * /finance/mandates/:id/mark-won). At least one field must be present.
 */
export const commissionUpdateSchema = z
    .object({
    commissionSide: commissionSideSchema.nullable(),
    achievedPrice: moneySchema.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
/**
 * Create a recurring fixed cost (POST /finance/monthly-costs). `activeFrom` is
 * the first month it applies; `activeTo` (optional) the last — both are stored
 * UTC-midnight-of-month and compared against the month window in the dashboard.
 */
export const monthlyCostCreateSchema = z
    .object({
    label: labelSchema,
    amount: moneySchema,
    activeFrom: z.coerce.date(),
    activeTo: z.coerce.date().nullable().optional(),
})
    .refine((v) => !v.activeTo || v.activeTo >= v.activeFrom, {
    message: 'active_to_before_from',
    path: ['activeTo'],
});
/** Partial update of a monthly fixed cost (PATCH /finance/monthly-costs/:id). */
export const monthlyCostUpdateSchema = z
    .object({
    label: labelSchema,
    amount: moneySchema,
    activeFrom: z.coerce.date(),
    activeTo: z.coerce.date().nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
/**
 * Book an actual incoming payment (POST /finance/payments) — the admin records
 * money received, optionally linked to a mandate (a commission) and prefilled
 * with its expected commission. `receivedAt` drives the monthly bucketing.
 */
export const financePaymentCreateSchema = z.object({
    amount: moneySchema,
    receivedAt: z.coerce.date(),
    mandateId: z.string().uuid().nullable().optional(),
    note: z.string().trim().max(500).nullable().optional(),
});
/** Partial update of a booked payment (PATCH /finance/payments/:id). */
export const financePaymentUpdateSchema = z
    .object({
    amount: moneySchema,
    receivedAt: z.coerce.date(),
    mandateId: z.string().uuid().nullable(),
    note: z.string().trim().max(500).nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' });
